import type { HydratedImage } from "../generated/api/types.gen";
import { Button } from "./Button";
import { LoadingSpinner } from "./LoadingSpinner";

interface ImageGridItemProps {
  image?: HydratedImage;
  index: number;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: () => void;
  onEnterRefinementMode?: (index: number) => void;
}

export function ImageGridItem({
  image,
  index,
  isSelected,
  isLoading,
  onSelect,
  onEnterRefinementMode,
}: ImageGridItemProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: contains nested button (Select and Refine), so cannot be a button itself
    <div
      role="button"
      tabIndex={0}
      onClick={!isLoading ? onSelect : undefined}
      onKeyDown={
        !isLoading
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onSelect();
            }
          : undefined
      }
      className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all w-full h-full ${
        isSelected
          ? "ring-0 outline-none"
          : "hover:ring-2 hover:ring-[var(--accent-primary)]"
      }`}
    >
      {image ? (
        <>
          <img
            src={image.url}
            alt={`Option ${index + 1}`}
            className="w-full h-full object-contain"
          />
          {onEnterRefinementMode && (
            <div
              className={`absolute top-4 right-4 transition-opacity ${
                isSelected
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
              }`}
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onEnterRefinementMode(index);
                }}
                variant="secondary"
                className="text-black text-sm px-3 py-2"
              >
                Select and Refine
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="w-full h-full">
          {isLoading ? (
            <div className="w-full h-full bg-[var(--bg-field)] rounded-xl flex items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-[var(--text-secondary)]">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-label="No image"
                  role="img"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
